import { InterestRateType, LoanType } from '@appTypes/LoansApi.interface'
import { ScoredLoanProduct } from '@appTypes/PagesApi.interface'

import formatNumberToCurrency from './formatNumberToCurrency'
import { formatLoanTermYears } from './loanDetailsFormUtils'
import { truncateToOneDecimalPlace } from './truncateToOneDecimalPlace'

export interface GetRepresentativeExampleTextOptions {
    annualInterestRate?: number
    apr: number
    arrangementFee?: number
    arrangementFeePercentage?: number
    finalPayment?: number
    interestRateType: InterestRateType
    loanAmount: number
    loanTerm: number
    monthlyPayment?: number
    optionToPurchaseFee?: number
    providerName?: string
    totalRepayable?: number
    type: LoanType
}

interface AdditionalInfoTextOptions {
    showArrangementFeeInfo: boolean
    showFinalRepaymentInfo: boolean
    showPurchaseFeeOptionInfo: boolean
}

const getAdditionalInfoText = (
    loan: GetRepresentativeExampleTextOptions,
    {
        showArrangementFeeInfo,
        showPurchaseFeeOptionInfo,
        showFinalRepaymentInfo,
    }: AdditionalInfoTextOptions
): string | null => {
    const getArrangementFee = () => {
        if (loan.arrangementFee) {
            return formatNumberToCurrency(loan.arrangementFee)
        }
        if (loan.arrangementFeePercentage) {
            return formatNumberToCurrency(
                loan.arrangementFeePercentage * 0.01 * loan.loanAmount
            )
        }
        return null
    }

    const getFinalPayment = formatNumberToCurrency(loan.finalPayment)

    const getPurchaseFee = () => {
        if (showPurchaseFeeOptionInfo) {
            return formatNumberToCurrency(loan.optionToPurchaseFee)
        }
        if (loan.finalPayment != null && loan.monthlyPayment != null) {
            return formatNumberToCurrency(
                loan.finalPayment - loan.monthlyPayment
            )
        }
        return null
    }

    if (
        showArrangementFeeInfo &&
        showPurchaseFeeOptionInfo &&
        showFinalRepaymentInfo
    ) {
        return `. This includes an arrangement fee of ${getArrangementFee()} and a final repayment of ${getFinalPayment}, which includes the ${getPurchaseFee()} option to purchase fee`
    }

    if (showArrangementFeeInfo && showFinalRepaymentInfo) {
        return (
            ` which includes an arrangement fee of ${getArrangementFee()}` +
            `. This includes a final repayment of ${getFinalPayment}, which includes the ${getPurchaseFee()} option to purchase fee`
        )
    }

    if (showArrangementFeeInfo && showPurchaseFeeOptionInfo) {
        return `. This includes an arrangement fee of ${getArrangementFee()}, which includes the ${getPurchaseFee()} option to purchase fee`
    }

    if (showArrangementFeeInfo) {
        return ` which includes an arrangement fee of ${getArrangementFee()}`
    }

    if (showFinalRepaymentInfo) {
        return `. This includes a final repayment of ${getFinalPayment}, which includes the ${getPurchaseFee()} option to purchase fee`
    }

    return null
}

export const getRepresentativeExampleOptionsForLoan = (
    loan: ScoredLoanProduct
): GetRepresentativeExampleTextOptions => {
    const loanDetails = loan.productDetails

    return {
        apr: loan.apr ?? loanDetails.apr,
        interestRateType: loanDetails.interestRateType,
        loanAmount: loan.loanAmount,
        loanTerm: loan.loanTerm,
        monthlyPayment: loan.monthlyPayment,
        providerName: loan.providerName,
        totalRepayable: loan.totalRepayable,
        type: loanDetails.type,
        annualInterestRate:
            loan.annualInterestRate ?? loanDetails.annualInterestRate,
        arrangementFee: loan.arrangementFee,
        arrangementFeePercentage: loanDetails.arrangementFeePercentage,
        finalPayment: loan.finalPayment,
        optionToPurchaseFee: loan.optionToPurchaseFee,
    }
}

export const getRepresentativeExampleText = (
    loan: GetRepresentativeExampleTextOptions,
    options: { isScored: boolean }
): string => {
    const borrowingText = options.isScored
        ? 'Borrowing'
        : 'Assumed borrowing of'
    const amount = formatNumberToCurrency(loan.loanAmount)
    const annualRate = truncateToOneDecimalPlace(
        loan.annualInterestRate || loan.apr
    )
    const rateType =
        loan.interestRateType === InterestRateType.Variable
            ? 'variable'
            : 'fixed'
    const apr = truncateToOneDecimalPlace(loan.apr)
    const aprType = loan.type === LoanType.Secured ? 'APRC' : 'APR'
    const monthlyPayment = formatNumberToCurrency(
        loan.monthlyPayment,
        '$0,0[.]00'
    )
    const totalRepayable = formatNumberToCurrency(
        loan.totalRepayable,
        '$0,0[.]00'
    )

    const showArrangementFee =
        !!loan.arrangementFee || !!loan.arrangementFeePercentage
    const showFinalRepaymentInfo = !!(
        loan.optionToPurchaseFee &&
        loan.finalPayment &&
        loan.monthlyPayment &&
        loan.finalPayment > loan.monthlyPayment
    )
    const showPurchaseFeeOptionInfo = !!loan.optionToPurchaseFee
    const showAdditionalText =
        showArrangementFee ||
        showFinalRepaymentInfo ||
        showPurchaseFeeOptionInfo

    const additionalInfo = getAdditionalInfoText(loan, {
        showArrangementFeeInfo: showArrangementFee,
        showFinalRepaymentInfo: showFinalRepaymentInfo,
        showPurchaseFeeOptionInfo: showPurchaseFeeOptionInfo,
    })

    return (
        `${borrowingText} ${amount} over ${formatLoanTermYears(loan.loanTerm)} at an annual interest rate of ${annualRate}% (${rateType})` +
        ` would result in a representative rate of ${apr}% ${aprType}, monthly repayments of ${monthlyPayment},` +
        ` total amount repayable is ${totalRepayable}${
            showAdditionalText ? additionalInfo : ''
        }.`
    )
}
